import type { SiteChatPlacement } from '@/constants/siteChat';

/**
 * Client-side bridge to the bots server's launcher API
 * (`window.easyPeasyChat`, see web-app `app/script/src/api.ts`).
 */
export interface SiteChatContact {
  email?: string;
  name?: string;
  externalId?: string;
}

export interface EasyPeasyChatApi {
  hidden?: boolean;
  /** Pre-load contact hint; the launcher reads it when it mounts. */
  contact?: SiteChatContact | null;
  setContact(contact: SiteChatContact | null): void;
  open(): void;
  close(): void;
  toggle(): void;
  show(): void;
  hide(): void;
  destroy(): void;
  init(): void;
  isOpen(): boolean;
  isVisible(): boolean;
  isMounted(): boolean;
}

declare global {
  interface Window {
    easyPeasyChat?: Partial<EasyPeasyChatApi>;
  }
}

/** Fired by the launcher once its DOM exists. */
export const SITE_CHAT_READY_EVENT = 'easy-peasy-chat:ready';

const hasLauncherApi = (
  api: Partial<EasyPeasyChatApi> | undefined,
): api is EasyPeasyChatApi => typeof api?.show === 'function';

/** The `<script>` the support bubble injects; its presence = we own the launcher. */
export const SITE_CHAT_SCRIPT_ID = 'site-chat-widget';

/**
 * The launcher API is one shared global: whichever `chat.min.js` ran last owns
 * `window.easyPeasyChat`. Pages that embed a different widget (the agent
 * preview) load their own launcher, so the support bubble may only drive the
 * global while its own script tag is on the page.
 */
const ownsLauncher = () =>
  typeof document !== 'undefined' &&
  !!document.getElementById(SITE_CHAT_SCRIPT_ID);

/**
 * Hides the support bubble while a dialog is open over the page (on phones
 * it sits on top of the dialog's buttons) and returns a function that brings
 * it back. Does nothing when the bubble isn't ours or isn't showing.
 */
export function hideSiteChatWhileOpen(): () => void {
  const api = typeof window === 'undefined' ? undefined : window.easyPeasyChat;
  if (!ownsLauncher() || !hasLauncherApi(api)) return () => undefined;
  if (typeof api.isVisible === 'function' && !api.isVisible()) {
    return () => undefined;
  }
  api.hide();
  return () => {
    const current = window.easyPeasyChat;
    if (ownsLauncher() && hasLauncherApi(current)) current.show();
  };
}

/**
 * Sets the pre-load flag the launcher reads when it mounts, so a page where
 * the bubble must be hidden never flashes it before the first `hide()`.
 * On 'unmount' routes the next launcher to mount is the PAGE's, so the flag
 * is cleared instead (a leftover `true` would start that widget hidden).
 */
export function preconfigureSiteChat(placement: SiteChatPlacement): void {
  if (typeof window === 'undefined') return;
  const api = (window.easyPeasyChat ||= {});
  api.hidden = placement === 'hide';
}

/**
 * Applies a placement to the launcher. Falls back to toggling the launcher's
 * DOM directly while the bots server still serves a `chat.min.js` without the
 * JavaScript API (kopi deployed before web-app).
 */
export function applySiteChatPlacement(placement: SiteChatPlacement): void {
  if (typeof window === 'undefined') return;
  preconfigureSiteChat(placement);
  if (!ownsLauncher()) return;
  const api = window.easyPeasyChat;
  if (placement === 'unmount') {
    if (hasLauncherApi(api)) api.destroy();
    else legacyApplyPlacement(placement);
    // Hand the global over to the page's own launcher; the bubble re-injects
    // a fresh script the next time a route wants it.
    document.getElementById(SITE_CHAT_SCRIPT_ID)?.remove();
    return;
  }
  if (hasLauncherApi(api)) {
    if (placement === 'show') {
      api.init();
      api.show();
    } else {
      api.hide();
    }
    return;
  }
  legacyApplyPlacement(placement);
}

function legacyApplyPlacement(placement: SiteChatPlacement): void {
  const panel = document.getElementById('dialoq');
  const launcher = document.getElementById('dialoq-btn')?.parentElement;
  const bubbles = document.getElementById('dialoq-message-bubbles');
  if (placement === 'show') {
    if (launcher) launcher.style.display = 'block';
    return;
  }
  if (panel) panel.style.display = 'none';
  if (launcher) launcher.style.display = 'none';
  if (bubbles) bubbles.style.display = 'none';
  if (placement === 'unmount') {
    panel?.remove();
    launcher?.remove();
  }
}

/** Opens the support agent, e.g. from a "Chat with us" button. */
export function openSiteChat(): boolean {
  const api = typeof window === 'undefined' ? undefined : window.easyPeasyChat;
  if (!ownsLauncher() || !hasLauncherApi(api)) return false;
  api.show();
  api.open();
  return true;
}

/**
 * Tags the visitor's conversations with the signed-in account (email/name)
 * so the support agent's Conversations view shows who was chatting. This is
 * an unverified attribution hint — the same trust level as a lead form — and
 * the bots server stores it as such.
 */
export function setSiteChatContact(contact: SiteChatContact | null): void {
  if (typeof window === 'undefined') return;
  const api = (window.easyPeasyChat ||= {});
  if (typeof api.setContact === 'function') {
    api.setContact(contact);
  } else {
    // Launcher not loaded yet: the pre-load field is picked up on mount.
    api.contact = contact;
  }
}
